import { useState } from "react";
import { Sparkles, Crown, Loader2, X, Check, Star, Heart } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCredits } from "@/hooks/useCredits";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { GoldDustLoader } from "./GoldDustLoader";
import { AnimatePresence } from "framer-motion";

interface MagicShopProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  priceId?: string;
  popular?: boolean;
  savings?: string;
  icon: React.ReactNode;
  description: string;
}

const packages: CreditPackage[] = [
  {
    id: "single",
    name: "1 saga",
    credits: 1,
    price: 29,
    icon: <Heart className="w-6 h-6" />,
    description: "När magin behövs nu",
  },
  {
    id: "small",
    name: "3 sagor",
    credits: 3,
    price: 69,
    savings: "Spara 21%",
    icon: <Heart className="w-6 h-6" />,
    description: "Perfekt för en helg",
  },
  {
    id: "medium",
    name: "5 sagor",
    credits: 5,
    price: 89,
    popular: true,
    savings: "Spara 39%",
    icon: <Crown className="w-6 h-6" />,
    description: "Bästa värdet",
  },
];

export const MagicShop = ({ open, onOpenChange }: MagicShopProps) => {
  const { balance, isPremium, refetchBalance } = useCredits();
  const { toast } = useToast();
  const [loadingPackage, setLoadingPackage] = useState<string | null>(null);
  const [showLoader, setShowLoader] = useState(false);

  const handlePurchase = async (pkg: CreditPackage) => {
    setLoadingPackage(pkg.id);
    setShowLoader(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          packageId: pkg.id,
          credits: pkg.credits,
          price: pkg.price,
          type: "one_time",
        },
      });

      if (error) throw error;

      if (data?.url) {
        // Open Stripe checkout in new tab (works better in preview/iframe)
        const checkoutWindow = window.open(data.url, '_blank');
        
        if (!checkoutWindow) {
          // Fallback if popup is blocked
          window.location.href = data.url;
        } else {
          // Close dialog and reset state since checkout opened in new tab
          onOpenChange(false);
          setShowLoader(false);
          setLoadingPackage(null);
          toast({
            title: "Checkout öppnad",
            description: "Slutför ditt köp i den nya fliken.",
          });
        }
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error) {
      console.error("Purchase error:", error);
      setShowLoader(false);
      toast({
        title: "Köpet misslyckades",
        description: "Kunde inte starta betalningen. Försök igen.",
        variant: "destructive",
      });
      setLoadingPackage(null);
    }
  };

  const handleSubscribe = async () => {
    setLoadingPackage("subscription");
    setShowLoader(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          packageId: "sagoklubben",
          credits: 5,
          price: 59,
          type: "subscription",
        },
      });

      if (error) throw error;

      if (data?.url) {
        // Open Stripe checkout in new tab (works better in preview/iframe)
        const checkoutWindow = window.open(data.url, '_blank');
        
        if (!checkoutWindow) {
          // Fallback if popup is blocked
          window.location.href = data.url;
        } else {
          // Close dialog and reset state since checkout opened in new tab
          onOpenChange(false);
          setShowLoader(false);
          setLoadingPackage(null);
          toast({
            title: "Checkout öppnad",
            description: "Slutför din prenumeration i den nya fliken.",
          });
        }
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error) {
      console.error("Subscription error:", error);
      setShowLoader(false);
      toast({
        title: "Prenumerationen misslyckades",
        description: "Kunde inte starta prenumerationen. Försök igen.",
        variant: "destructive",
      });
      setLoadingPackage(null);
    }
  };

  return (
    <>
      {/* Gold dust loader overlay */}
      <AnimatePresence>
        <GoldDustLoader isVisible={showLoader} />
      </AnimatePresence>

      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center pb-2">
          <DialogTitle className="text-2xl font-display flex items-center justify-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            Sagobutiken
          </DialogTitle>
        </DialogHeader>

        {/* Credit packages */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative bg-card border rounded-3xl p-4 flex flex-col items-center text-center transition-all hover:border-teal/50 hover:shadow-elevated ${
                pkg.popular ? "border-orange ring-2 ring-orange/20" : "border-border"
              }`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange text-white text-xs font-medium px-3 py-1 rounded-full">
                  Populär
                </div>
              )}
              
              <div className="w-12 h-12 rounded-2xl bg-teal/10 flex items-center justify-center text-teal mb-3">
                {pkg.icon}
              </div>
              
              <h3 className="font-display font-bold text-lg">{pkg.name}</h3>
              <p className="text-sm text-muted-foreground mb-2">{pkg.description}</p>
              
              <div className="text-3xl font-bold mb-1">
                {pkg.credits} <span className="text-base font-normal text-muted-foreground">{pkg.credits > 1 ? "sagor" : "saga"}</span>
              </div>
              
              <div className="text-xl font-semibold text-primary mb-1">
                {pkg.price} kr
              </div>
              
              {pkg.savings && (
                <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full mb-3">
                  {pkg.savings}
                </span>
              )}
              
              <Button
                onClick={() => handlePurchase(pkg)}
                disabled={loadingPackage !== null}
                className="w-full mt-auto"
                variant={pkg.popular ? "cta" : "outline"}
              >
                {loadingPackage === pkg.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Köp nu"
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* Subscription section */}
        <div className="relative mt-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-background text-muted-foreground">
              eller prenumerera
            </span>
          </div>
        </div>

        <div className={`relative mt-4 border-2 rounded-3xl p-4 sm:p-6 transition-all overflow-hidden ${
          isPremium 
            ? "border-gold/50 bg-gold/10" 
            : "border-gold/60 bg-card shadow-lg shadow-gold/20 ring-1 ring-gold/30"
        }`}>
          {/* Decorative glow */}
          <div className="absolute top-4 right-4 opacity-20">
            <Sparkles className="w-12 h-12 text-gold" />
          </div>
          
          <div className="flex flex-col items-center gap-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-gold via-orange to-gold flex items-center justify-center text-white shrink-0 shadow-lg shadow-gold/30">
              <Crown className="w-7 h-7 sm:w-8 sm:h-8" />
            </div>
            
            <div className="flex-1 text-center w-full">
              <h3 className="font-display font-bold text-xl sm:text-2xl text-foreground">
                Sagoklubben
              </h3>
              <div className="mt-1">
                <span className="text-lg font-bold text-gold-dark bg-gold/20 px-3 py-1 rounded-full whitespace-nowrap">
                  59 kr/mån
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Månadsabonnemang</p>
              
              <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-x-4 gap-y-1.5 mt-3 text-sm">
                <span className="flex items-center justify-center gap-1.5 text-foreground font-medium">
                  <Check className="w-4 h-4 text-green-600 shrink-0" />
                  5 sagor varje månad
                </span>
                <span className="flex items-center justify-center gap-1.5 text-foreground font-medium">
                  <Check className="w-4 h-4 text-green-600 shrink-0" />
                  Ingen bindningstid
                </span>
                <span className="flex items-center justify-center gap-1.5 text-foreground font-medium">
                  <Check className="w-4 h-4 text-green-600 shrink-0" />
                  Alla stilar
                </span>
              </div>
            </div>
            
            <Button
              onClick={handleSubscribe}
              disabled={loadingPackage !== null || isPremium}
              variant="cta"
              size="lg"
              className="shadow-lg shadow-orange/30 w-full sm:w-auto whitespace-nowrap"
            >
              {loadingPackage === "subscription" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isPremium ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Aktiv
                </>
              ) : (
                <>
                  <Crown className="w-4 h-4 mr-2" />
                  Gå med
                </>
              )}
            </Button>
          </div>
        </div>

        <p className="text-xs text-center text-muted-foreground mt-4">
          ✨ Köpta krediter förfaller aldrig
        </p>
      </DialogContent>
    </Dialog>
    </>
  );
};
